<template>
    <v-row class="ma-0 pb-offset">
        <v-col cols="12" class="pa-1">
            <v-card :elevation="1" class="background-2 round-card">
                <v-card-title class="pa-3">
                    <p class="pl-2 pl-xl-3">Storico Movimenti Ore</p>
                    <p v-if="entityTitle" class="entity-title">&nbsp;&nbsp;{{ entityTitle }}</p>
                    <v-spacer></v-spacer>
                    <v-btn color="success" small class="mr-2" @click="openDialog('topup')" :disabled="loading">
                        <v-icon left>mdi-plus</v-icon>
                        Aggiungi Ore
                    </v-btn>
                    <v-btn color="warning" small class="mr-2" @click="openDialog('reduction')" :disabled="loading">
                        <v-icon left>mdi-minus</v-icon>
                        Riduci Ore
                    </v-btn>
                    <v-btn color="info" small @click="recalculate" :loading="recalculating">
                        <v-icon left>mdi-refresh</v-icon>
                        Ricalcola
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text>
                    <v-row class="mb-4">
                        <v-col cols="12" md="3">
                            <v-card outlined class="pa-3 text-center round-card">
                                <div class="text-caption grey--text">Ore Totali</div>
                                <div class="text-h5 font-weight-bold">{{ totalHours }}</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-card outlined class="pa-3 text-center round-card">
                                <div class="text-caption grey--text">Ore Rimanenti</div>
                                <div class="text-h5 font-weight-bold" :class="leftHoursColor">{{ leftHours }}</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-card outlined class="pa-3 text-center round-card">
                                <div class="text-caption grey--text">Ore Consumate</div>
                                <div class="text-h5 font-weight-bold">{{ consumedHours }}</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-card outlined class="pa-3 text-center round-card">
                                <div class="text-caption grey--text">Ore Extra</div>
                                <div class="text-h5 font-weight-bold red--text">{{ extraHours }}</div>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-data-table
                        :headers="headers"
                        :items="movements"
                        :loading="loading"
                        sort-by="sort_date"
                        sort-desc
                        class="background-2 enhanced-table"
                        fixed-header
                        :mobile-breakpoint="0"
                        :footer-props="{
                            'items-per-page-options': [10, 25, 50, 100, { text: 'Tutti', value: -1 }],
                            showFirstLastPage: true
                        }"
                    >
                        <template v-slot:no-data>
                            <v-container v-if="!loading" class="text-center py-8">
                                <v-icon size="64" color="grey lighten-2">mdi-clock-outline</v-icon>
                                <h3 class="mt-4 grey--text">Nessun movimento registrato</h3>
                                <p class="grey--text text--lighten-1">I movimenti del carnet appariranno qui</p>
                            </v-container>
                        </template>

                        <template v-slot:item.type="{ item }">
                            <v-chip small :color="typeColor(item.type)" dark>{{ typeLabel(item.type) }}</v-chip>
                        </template>
                        <template v-slot:item.description="{ item }">
                            <a v-if="item.trouble_route" href="#" @click.prevent="openTroubleTab(item.trouble_route)">{{ item.description }}</a>
                            <span v-else>{{ item.description }}</span>
                        </template>
                        <template v-slot:item.hours="{ item }">
                            <span :class="item.hours > 0 ? 'success--text font-weight-bold' : 'error--text font-weight-bold'">
                                {{ item.hours > 0 ? '+' : '' }}{{ formatHoursMinutes(item.hours) }}
                            </span>
                        </template>
                    </v-data-table>
                </v-card-text>

                <v-dialog v-model="dialog" max-width="600" persistent>
                    <v-card class="background-2 round-card">
                        <v-card-title class="pa-3">
                            <p class="pl-2 pl-xl-3">{{ dialogType === 'topup' ? 'Aggiungi Ore' : 'Riduci Ore' }}</p>
                            <v-spacer></v-spacer>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pa-4">
                            <v-text-field
                                v-model="form.hours"
                                label="Ore"
                                v-mask="'##:##'"
                                suffix="h"
                                placeholder="00:00"
                                :rules="[
                                    v => !!v || 'Campo obbligatorio',
                                    v => /^([0-9]{1,2}):([0-5][0-9])$/.test(v) && v !== '00:00' || 'Formato richiesto hh:mm, maggiore di 0'
                                ]"
                                outlined
                                dense
                            ></v-text-field>
                            <v-textarea
                                v-model="form.description"
                                label="Descrizione"
                                :rules="[v => !!v || 'La descrizione è obbligatoria']"
                                outlined
                                dense
                                rows="3"
                            ></v-textarea>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn text color="red" @click="closeDialog">Annulla</v-btn>
                            <v-btn
                                text
                                :color="dialogType === 'topup' ? 'success' : 'warning'"
                                @click="submitMovement"
                                :loading="submitting"
                                :disabled="!isFormValid"
                            >
                                Conferma
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
export default {
    name: 'carnet-movements',

    props: {
        id: Number,
        apiPrefix: {
            type: String,
            default: 'contracts'
        },
        entityTitle: String,
    },

    data() {
        return {
            loading: false,
            recalculating: false,
            submitting: false,
            dialog: false,
            dialogType: 'topup',
            movements: [],
            contractData: {},
            extraHoursValue: 0,
            form: {
                hours: '',
                description: '',
            },
            headers: [
                { text: 'Data', value: 'created_at', width: '160px', sort: (a, b) => {
                    const itemA = this.movements.find(m => m.created_at === a)
                    const itemB = this.movements.find(m => m.created_at === b)
                    if (!itemA || !itemB) return 0
                    return itemA.sort_date < itemB.sort_date ? -1 : itemA.sort_date > itemB.sort_date ? 1 : 0
                }},
                { text: 'Tipo', value: 'type', width: '140px' },
                { text: 'Ore', value: 'hours', width: '120px' },
                { text: 'Descrizione', value: 'description' },
                { text: 'Utente', value: 'user_name', width: '160px' },
            ],
        }
    },

    computed: {
        totalHours() {
            return this.contractData.total_hours != null ? this.formatHoursMinutes(parseFloat(this.contractData.total_hours)) : '-'
        },
        leftHours() {
            return this.contractData.left_hours != null ? this.formatHoursMinutes(parseFloat(this.contractData.left_hours)) : '-'
        },
        consumedHours() {
            const consumed = this.movements
                .filter(m => m.type === 'consumption')
                .reduce((sum, m) => sum + Math.abs(m.hours), 0)
            return this.formatHoursMinutes(consumed)
        },
        extraHours() {
            return this.extraHoursValue > 0 ? this.formatHoursMinutes(this.extraHoursValue) : '0h'
        },
        leftHoursColor() {
            if (this.contractData.left_hours == null) return ''
            const left = parseFloat(this.contractData.left_hours)
            if (left <= 0) return 'red--text'
            if (left < parseFloat(this.contractData.total_hours) * 0.2) return 'orange--text'
            return 'green--text'
        },
        isFormValid() {
            if (!this.form.description) return false
            if (!this.form.hours) return false
            if (!/^([0-9]{1,2}):([0-5][0-9])$/.test(this.form.hours)) return false
            return this.form.hours !== '00:00'
        }
    },

    created() {
        this.fetchData()
    },

    methods: {
        fetchData() {
            this.loading = true
            Promise.all([
                this.axios.get(this.apiPrefix + '/' + this.id + '/movements'),
                this.axios.get(this.apiPrefix + '/' + this.id + '?lite=1'),
            ]).then(([movementsRes, contractRes]) => {
                this.movements = movementsRes.data.data
                this.extraHoursValue = parseFloat(movementsRes.data.extra_hours || 0)
                this.contractData = contractRes.data.model
            }).finally(() => {
                this.loading = false
            })
        },

        openDialog(type) {
            this.dialogType = type
            this.form = { hours: '', description: '' }
            this.dialog = true
        },

        closeDialog() {
            this.dialog = false
            this.form = { hours: '', description: '' }
        },

        parseHhMmToHours(hhmm) {
            const [h, m] = hhmm.split(':').map(n => parseInt(n, 10))
            return h + m / 60
        },

        submitMovement() {
            this.submitting = true
            this.axios.post(this.apiPrefix + '/' + this.id + '/movements', {
                type: this.dialogType,
                hours: this.parseHhMmToHours(this.form.hours),
                description: this.form.description,
            }).then(response => {
                this.closeDialog()
                this.fetchData()
                this.$store.commit('showNotification', {
                    text: response.data.message || 'Movimento aggiunto',
                    type: 'success'
                })
            }).catch(error => {
                const message = error.response && error.response.data ? error.response.data.message : 'Errore durante il salvataggio'
                this.$store.commit('showNotification', {
                    text: message,
                    type: 'error'
                })
            }).finally(() => {
                this.submitting = false
            })
        },

        recalculate() {
            this.recalculating = true
            this.axios.post(this.apiPrefix + '/' + this.id + '/recalculate').then(response => {
                this.fetchData()
                this.$store.commit('showNotification', {
                    text: response.data.message || 'Ricalcolo completato',
                    type: 'success'
                })
            }).catch(error => {
                const message = error.response && error.response.data ? error.response.data.message : 'Errore durante il ricalcolo'
                this.$store.commit('showNotification', {
                    text: message,
                    type: 'error'
                })
            }).finally(() => {
                this.recalculating = false
            })
        },

        openTroubleTab(route) {
            const url = this.$router.resolve({ path: route }).href
            window.open(url, '_blank')
        },

        formatHoursMinutes(decimalHours) {
            const h = Math.floor(Math.abs(decimalHours))
            const m = Math.round((Math.abs(decimalHours) - h) * 60)
            const sign = decimalHours < 0 ? '-' : ''
            return m > 0 ? sign + h + 'h ' + m + 'm' : sign + h + 'h'
        },

        typeColor(type) {
            const colors = {
                initial: 'blue',
                topup: 'success',
                reduction: 'warning',
                adjustment: 'grey',
                consumption: 'red',
                warranty: 'purple',
            }
            return colors[type] || 'grey'
        },

        typeLabel(type) {
            const labels = {
                initial: 'Iniziale',
                topup: 'Aggiunta',
                reduction: 'Riduzione',
                adjustment: 'Rettifica',
                consumption: 'Intervento',
                warranty: 'Garanzia',
            }
            return labels[type] || type
        },
    }
}
</script>
